import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    useBreakpointValue,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Input,
    InputGroup,
    InputLeftElement,
    useToast,
    IconButton,
    Collapse,
} from '@chakra-ui/react';
import {
    FaBook,
    FaUsers,
    FaSync,
    FaUser,
    FaMapMarkerAlt,
    FaCalendar,
    FaSearch,
    FaChartLine,
    FaShieldAlt,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMownBookPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [rmData, setRmData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const toast = useToast();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const lightBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.2)' : 'gray.100';

    // Info card backgrounds - improved transparency for dark theme
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '10';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Icon colors
    const mutedIconColor = isDarkTheme ? mutedTextColor : 'gray.400';

    // Responsive breakpoint
    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    const fetchRMownBook = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/content/rm-own-book`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setRmData(response.data);
        } catch (err) {
            console.error('Error fetching RM own book:', err);
            const errorMsg = err.response?.data?.message || 'Failed to fetch customer book';
            setError(errorMsg);
            toast({
                title: 'Error',
                description: errorMsg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchRMownBook();
        }
        // eslint-disable-next-line
    }, [token]);

    const getRiskColor = (riskProfile) => {
        if (riskProfile <= 3) return 'green';
        if (riskProfile <= 6) return 'yellow';
        if (riskProfile <= 8) return 'orange';
        return 'red';
    };

    const getRiskLabel = (riskProfile) => {
        if (riskProfile <= 3) return 'Low';
        if (riskProfile <= 6) return 'Medium';
        if (riskProfile <= 8) return 'High';
        return 'Very High';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const customers = rmData?.customers || [];
    const customerCount = rmData?.customer_count || 0;
    const rmName = rmData?.rm_name || 'RM';

    // Filter customers based on search
    const filteredCustomers = customers.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.country_iso.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Calculate statistics
    const avgRisk = customers.length > 0
        ? (customers.reduce((sum, c) => sum + c.risk_profile, 0) / customers.length).toFixed(1)
        : 0;

    const highRiskCount = customers.filter(c => c.risk_profile >= 7).length;

    // Toggle customer details
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        // Search is handled by filteredCustomers already
    };

    return (
        <Box
            minH="100vh"
            width="100%"
            pb={{ base: "90px", md: "20px" }}
            bg={backgroundColor}
        >
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient={isDarkTheme 
                                ? `linear(to-r, ${accentColor}, ${primaryColor})` 
                                : `linear(to-r, ${primaryColor}, ${accentColor})`
                            }
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaBook} boxSize={{ base: 6, md: 8 }} />
                                <Text>My Customer Book</Text>
                            </HStack>
                        </Heading>
                        <Text color={bodyTextColor} fontSize={{ base: 'sm', md: 'md' }} px={{ base: 4, md: 0 }}>
                            View and manage your assigned customer portfolio
                        </Text>
                        {rmData && (
                            <HStack justify="center" mt={3} spacing={2}>
                                <Icon as={FaUser} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                <Text color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>
                                    {rmName}
                                </Text>
                            </HStack>
                        )}
                    </Box>

                    {/* Statistics Cards */}
                    {!loading && rmData && (
                        <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 3, md: 4 }}>
                            <Card
                                bg={cardBg}
                                borderRadius="xl"
                                boxShadow="sm"
                                border="1px"
                                borderColor={lightBorderColor}
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                Total Customers
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber
                                            fontSize={{ base: '2xl', md: '3xl' }}
                                            color={isDarkTheme ? accentColor : primaryColor}
                                            fontWeight="bold"
                                        >
                                            {customerCount}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card
                                bg={cardBg}
                                borderRadius="xl"
                                boxShadow="sm"
                                border="1px"
                                borderColor={lightBorderColor}
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaChartLine} color={isDarkTheme ? accentColor : accentColor} boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                Avg Risk
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber
                                            fontSize={{ base: '2xl', md: '3xl' }}
                                            color={isDarkTheme ? accentColor : accentColor}
                                            fontWeight="bold"
                                        >
                                            {avgRisk}/10
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card
                                bg={cardBg}
                                borderRadius="xl"
                                boxShadow="sm"
                                border="1px"
                                borderColor={lightBorderColor}
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaShieldAlt} color="orange.500" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                High Risk
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber
                                            fontSize={{ base: '2xl', md: '3xl' }}
                                            color="orange.600"
                                            fontWeight="bold"
                                        >
                                            {highRiskCount}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert
                            status="error"
                            borderRadius="xl"
                            boxShadow="sm"
                            variant="left-accent"
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle fontSize={{ base: 'sm', md: 'md' }} color={headingTextColor}>Error!</AlertTitle>
                                <AlertDescription fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>{error}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search and Refresh */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color={mutedIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID or Country..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor={borderColor}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    />
                                </InputGroup>
                                <Button
                                    leftIcon={<FaSync />}
                                    bg={primaryColor}
                                    color="white"
                                    onClick={fetchRMownBook}
                                    isLoading={loading}
                                    _hover={{ bg: buttonHoverBg }}
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Customer List */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor} fontSize="sm">Loading customers...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredCustomers.length === 0 ? (
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Box textAlign="center" py={10}>
                                    <Icon as={searchTerm ? FaSearch : FaBook} boxSize={{ base: 10, md: 12 }} color={mutedIconColor} mb={4} />
                                    <Text color={mutedTextColor} fontSize={{ base: 'md', md: 'lg' }} fontWeight="medium">
                                        {searchTerm ? `No customers found matching "${searchTerm}"` : 'No customers in your book yet'}
                                    </Text>
                                    {searchTerm && (
                                        <Button
                                            mt={4}
                                            variant="link"
                                            color={isDarkTheme ? accentColor : primaryColor}
                                            onClick={() => setSearchTerm('')}
                                        >
                                            Clear search
                                        </Button>
                                    )}
                                </Box>
                            </CardBody>
                        </Card>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {filteredCustomers.map((customer, index) => (
                                <Card
                                    key={customer.customer_id || index}
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    transition="all 0.2s"
                                    _hover={{ boxShadow: 'lg' }}
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Flex justify="space-between" align="center">
                                            <VStack align="start" spacing={1} flex={1}>
                                                <Heading size="sm" color={headingTextColor}>
                                                    {customer.customer_id}
                                                </Heading>
                                                <HStack spacing={4} flexWrap="wrap">
                                                    <HStack spacing={1}>
                                                        <Icon as={FaMapMarkerAlt} color={mutedIconColor} boxSize={3.5} />
                                                        <Text fontSize="sm" color={bodyTextColor}>
                                                            {customer.country_iso}
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={1}>
                                                        <Icon as={FaUser} color={mutedIconColor} boxSize={3.5} />
                                                        <Text fontSize="sm" color={bodyTextColor}>
                                                            {customer.age} years
                                                        </Text>
                                                    </HStack>
                                                    <Badge
                                                        colorScheme={getRiskColor(customer.risk_profile)}
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                    >
                                                        {getRiskLabel(customer.risk_profile)} ({customer.risk_profile}/10)
                                                    </Badge>
                                                </HStack>
                                            </VStack>
                                            <IconButton
                                                icon={
                                                    expandedCustomer === customer.customer_id ? (
                                                        <ChevronUpIcon />
                                                    ) : (
                                                        <ChevronDownIcon />
                                                    )
                                                }
                                                onClick={() => toggleCustomer(customer.customer_id)}
                                                colorScheme="blue"
                                                variant="ghost"
                                                aria-label="Toggle details"
                                            />
                                        </Flex>

                                        <Collapse in={expandedCustomer === customer.customer_id} animateOpacity>
                                            <Box mt={6} pt={6} borderTop="1px solid" borderColor={borderColor}>
                                                <VStack align="stretch" spacing={4}>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                        <Card 
                                                            bg={infoBg} 
                                                            borderRadius="md" 
                                                            boxShadow="sm" 
                                                            borderWidth="1px" 
                                                            borderColor={borderColor}
                                                        >
                                                            <CardBody>
                                                                <HStack spacing={2} mb={2}>
                                                                    <Icon as={FaUser} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                                                    <Text fontWeight="bold" color={labelTextColor}>Customer Details</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Customer ID:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                            {customer.customer_id}
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Age:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                            {customer.age} years
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Country:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                            {customer.country_iso}
                                                                        </Text>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>

                                                        <Card 
                                                            bg={infoBg} 
                                                            borderRadius="md" 
                                                            boxShadow="sm" 
                                                            borderWidth="1px" 
                                                            borderColor={borderColor}
                                                        >
                                                            <CardBody>
                                                                <HStack spacing={2} mb={2}>
                                                                    <Icon as={FaShieldAlt} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                                                    <Text fontWeight="bold" color={labelTextColor}>Risk Information</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Risk Profile:</Text>
                                                                        <Badge
                                                                            colorScheme={getRiskColor(customer.risk_profile)}
                                                                            px={3}
                                                                            py={1}
                                                                            borderRadius="full"
                                                                            fontSize="xs"
                                                                            fontWeight="bold"
                                                                        >
                                                                            {customer.risk_profile}/10
                                                                        </Badge>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Risk Level:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                            {getRiskLabel(customer.risk_profile)}
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Member Since:</Text>
                                                                        <HStack spacing={1}>
                                                                            <Icon as={FaCalendar} color={mutedIconColor} boxSize={3} />
                                                                            <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                                {formatDate(customer.created_at)}
                                                                            </Text>
                                                                        </HStack>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                </VStack>
                                            </Box>
                                        </Collapse>
                                    </CardBody>
                                </Card>
                            ))}
                        </VStack>
                    )}

                    {/* Summary footer */}
                    {!loading && filteredCustomers.length > 0 && (
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Text fontSize="sm" color={bodyTextColor} textAlign="center">
                                    Showing {filteredCustomers.length} of {customerCount} customers
                                </Text>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMownBookPaid;